import { isDelayed } from '@/components/patient/PatientSlot/PatientSlotInfo';

export type DelayType = 'oob' | 'pt' | 'ptComplete' | 'pacuOut';

export interface DelayCheckParams {
  patient: any;
  room: any;
  delayType: DelayType;
  showDelayReasons?: boolean;
}

export const shouldPromptForDelayReason = ({ patient, room, delayType }: DelayCheckParams): boolean => {
  if (!patient || !room || !delayType) return false;

  const events = patient.events || {};
  const timeEntered = events.recoveryAt || events.pacuAt;

  switch (delayType) {
    case 'oob':
      return (
        patient.transferredToChair &&
        isDelayed(timeEntered, patient.transferredToChair, room.oobWarningThreshold) &&
        !patient.oobWarningDelayReason
      );

    case 'pt':
      return (
        patient.transferredToPT &&
        isDelayed(timeEntered, patient.transferredToPT, room.ptWarningThreshold) &&
        !patient.ptWarningDelayReason
      );

    case 'ptComplete':
      return (
        patient.ptComplete &&
        isDelayed(timeEntered, patient.ptComplete, room.ptCompleteWarningThreshold) &&
        !patient.ptCompleteWarningDelayReason
      );

    case 'pacuOut':
      return (
        patient.dischargedAt &&
        isDelayed(timeEntered, patient.dischargedAt, room.pacuOutWarningThreshold) &&
        !patient.pacuOutWarningDelayReason
      );

    default:
      return false;
  }
};

export const shouldPromptBeforeAction = ({
  patient,
  room,
  delayType,
  showDelayReasons = false,
}: DelayCheckParams): boolean => {
  if (!patient || !room || !delayType || !showDelayReasons) return false;

  const events = patient.events || {};
  const timeEntered = events.recoveryAt || events.pacuAt;
  const currentTime = new Date();

  if (!timeEntered) return false;

  switch (delayType) {
    case 'oob':
      return (
        !patient.transferredToChair &&
        room.oobWarningThreshold &&
        isDelayed(timeEntered, currentTime, room.oobWarningThreshold)
      );

    case 'pt':
      return (
        patient.transferredToChair &&
        !patient.transferredToPT &&
        room.ptWarningThreshold &&
        isDelayed(timeEntered, currentTime, room.ptWarningThreshold)
      );

    case 'ptComplete':
      return (
        patient.transferredToPT &&
        !patient.ptComplete &&
        room.ptCompleteWarningThreshold &&
        isDelayed(timeEntered, currentTime, room.ptCompleteWarningThreshold)
      );

    case 'pacuOut':
      const pacuEntryTime = patient.events?.pacuAt || timeEntered;
      return (
        !patient.dischargedAt &&
        room.pacuOutWarningThreshold &&
        pacuEntryTime &&
        isDelayed(pacuEntryTime, currentTime, room.pacuOutWarningThreshold)
      );

    default:
      return false;
  }
};

export const getExistingDelayReason = (patient: any, delayType: DelayType): string => {
  if (!patient || !delayType) return '';

  switch (delayType) {
    case 'oob':
      return patient.oobWarningDelayReason || '';
    case 'pt':
      return patient.ptWarningDelayReason || '';
    case 'ptComplete':
      return patient.ptCompleteWarningDelayReason || '';
    case 'pacuOut':
      return patient.pacuOutWarningDelayReason || '';
    default:
      return '';
  }
};
